<template>
  <Detail class="survey-editor" :titleI18nKey="`admin.crm.survey.title.survey.${survey.survey_type}`">
    <div class="image-uploader">
      <div class="pc-image" :class="survey.img_for_pc || 'no-image'">
        <img class="image-previewer" :src="pcImagePath"/>
        <input
          v-show="false"
          ref="pcImageFile"
          type="file"
          :accept="acceptFileType.join(',')"
          @change="handleImageFileChange(true)"/>
        <div class="uploader-mask" @click="handleUploaderClick(true)">
          <div class="uploader">
            <div class="add-btn">
              <i class="el-icon-circle-plus"></i>
            </div>
            <div class="tip-message">
              <div class="tip-title">{{ $t('admin.crm.survey.upload.pc.tip') }}</div>
              <div class=tip-subtitle>{{ $t('admin.crm.survey.upload.pc.subTip') }}</div>
            </div>
          </div>
        </div>
      </div>
      <div class="mobile-image" :class="survey.img_for_mobile || 'no-image'">
        <img class="image-previewer" :src="mobileImagePath"/>
        <input
          v-show="false"
          ref="mobileImageFile"
          type="file"
          :accept="acceptFileType.join(',')"
          @change="handleImageFileChange(false)"/>
        <div class="uploader-mask" @click="handleUploaderClick(false)">
          <div class="uploader">
            <div class="add-btn">
              <i class="el-icon-circle-plus"></i>
            </div>
            <div class="tip-message">
              <div class="tip-title">{{ $t('admin.crm.survey.upload.mobile.tip') }}</div>
              <div class=tip-subtitle>{{ $t('admin.crm.survey.upload.mobile.subTip') }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="question-list-container">
      <draggable
        v-model="survey.questions"
        tag="div"
        handle=".drag-btn"
        animation="200"
        group="description"
        ghostClass="ghost"
        class="question-list"
      >
        <div
          v-for="(item, index) in survey.questions"
          class="list-item"
          :key="index">
          <div class="item-title">
            <div class="item-icon">
              <i class="iconfont" :class="getQuestionIcon(item)"></i>
            </div>
            <div class="item-name">
              {{ isEnLanguage ? item.en_title : item.cn_title }}
            </div>
            <div class="item-operator-container">
              <div class="drag-btn">
                <i class="iconfont iconMore"></i>
              </div>
              <div class="edit-btn" @click="handleEditQuestion(item, index)">
                <i class="el-icon-edit-outline"></i>
              </div>
              <div class="delete-btn" @click="handleDeleteQuestion(item, index)">
                <i class="iconfont iconDelete"></i>
              </div>
            </div>
          </div>
          <div v-if="item.option_type === 'custom'" class="item-content">
            <ul class="content-list">
              <li
                v-for="(optionItem, index) in item.options"
                class="content-item"
                :key="index">
                {{ isEnLanguage ? optionItem.en_value : optionItem.cn_value }}
              </li>
            </ul>
          </div>
        </div>
      </draggable>
      <div class="add-btn-group">
        <div class="add-select" @click="openSelectQuestionDialog">{{ $t('admin.crm.survey.btn.addQuestion.select') }}</div>
        <div class="add-input" @click="openTextQuestionDialog">{{ $t('admin.crm.survey.btn.addQuestion.text') }}</div>
      </div>
    </div>
    <div class="bottom-btn-group">
      <el-button class="cancel-btn" @click="handleCancel">{{ $t('common.btn.cancel') }}</el-button>
      <el-button class="save-btn" @click="handleSave">{{ $t('common.btn.save') }}</el-button>
    </div>
    <DialogWrapper
      :titleI18nKey="textDialogTitleI18nKey"
      close-on-cancel
      :show.sync="textQuestionDialog.show"
      :close-on-click-modal="false"
      @save="handleTextQuestionSave"
      @cancel="handleTextQuestionCancelOrClose"
      @closed="handleTextQuestionCancelOrClose"
    >
      <div class="text-question-dialog-body">
        <el-form ref="testQuestionForm" :model="textQuestionDialog.form" :rules="formRules" label-position="top">
          <el-form-item :label="$t('admin.crm.survey.label.question.cn')" prop="question.cn">
            <el-input
              v-model="textQuestionDialog.form.question.cn"
              type="textarea"
              :autosize="{ minRows: 4 }"
              placeholder=""></el-input>
          </el-form-item>
          <el-form-item :label="$t('admin.crm.survey.label.question.en')" prop="question.en">
            <el-input
              v-model="textQuestionDialog.form.question.en"
              type="textarea"
              :autosize="{ minRows: 4 }"
              placeholder=""></el-input>
          </el-form-item>
        </el-form>
      </div>
    </DialogWrapper>
    <DialogWrapper
      :titleI18nKey="selectDialogTitleI18nKey"
      close-on-cancel
      :show.sync="selectQuestionDialog.show"
      :close-on-click-modal="false"
      @save="handleSelectQuestionSave"
      @cancel="handleSelectQuestionCancelOrClose"
      @closed="handleSelectQuestionCancelOrClose"
    >
      <div class="select-question-dialog-body">
        <el-form ref="form" :model="selectQuestionDialog.form" :rules="formRules" label-position="top">
          <el-form-item :label="$t('admin.crm.survey.label.optionType')" prop="optionType">
            <div class="label-tip">
              <p>{{ $t('admin.crm.survey.label.optionTypeTip') }}</p>
            </div>
            <el-select
              v-if="selectQuestionDialog.isEdit"
              v-auto-tooltip.always="selectQuestionDialog.form.filterI18nName"
              disabled
              :value="selectQuestionDialog.form.filterI18nName"
            ></el-select>
            <template v-else>
              <el-select v-model="selectQuestionDialog.form.optionType" class="option-type-select">
                <el-option
                  v-for="(item, index) in optionTypeList"
                  :key="index"
                  :label="item.label"
                  :value="item.value">
                </el-option>
              </el-select>
            </template>
          </el-form-item>
          <el-form-item :label="$t('admin.crm.survey.label.question.cn')" prop="question.cn">
            <el-input
              v-model="selectQuestionDialog.form.question.cn"
              type="textarea"
              :autosize="{ minRows: 4 }"
              placeholder=""></el-input>
          </el-form-item>
          <el-form-item :label="$t('admin.crm.survey.label.question.en')" prop="question.en">
            <el-input
              v-model="selectQuestionDialog.form.question.en"
              type="textarea"
              :autosize="{ minRows: 4 }"
              placeholder=""></el-input>
          </el-form-item>
          <el-form-item :label="$t('admin.crm.survey.label.answerType')" prop="type">
            <el-radio-group v-model="selectQuestionDialog.form.type">
              <el-radio label="dropdown">{{ $t('admin.crm.survey.answerType.single') }}</el-radio>
              <el-radio label="checkbox">{{ $t('admin.crm.survey.answerType.multiple') }}</el-radio>
            </el-radio-group>
          </el-form-item>
          <template v-if="selectQuestionDialog.form.optionType === 'custom'">
            <el-form-item
              :label="$t('admin.crm.survey.label.customOptions.cn')"
              prop="customOptions.cn">
              <el-input
                v-model="selectQuestionDialog.form.customOptions.cn"
                type="textarea"
                :autosize="{ minRows: 4 }"
                placeholder=""></el-input>
            </el-form-item>
            <el-form-item
              :label="$t('admin.crm.survey.label.customOptions.en')"
              prop="customOptions.en">
              <el-input
                v-model="selectQuestionDialog.form.customOptions.en"
                type="textarea"
                :autosize="{ minRows: 4 }"
                placeholder=""></el-input>
            </el-form-item>
          </template>
        </el-form>
      </div>
    </DialogWrapper>
  </Detail>
</template>

<script>
import _ from 'lodash'
import { mapActions, mapGetters } from 'vuex'
import draggable from 'vuedraggable'
import * as qiniu from 'qiniu-js'
import { STATIC_OPTION_TYPE, ACCEPT_FILE_TYPE, ACCEPT_FILE_SIZE } from '@/constant/survey'
import DialogWrapper from '@/components/dialog/dialogWrapper.vue'
import Detail from '../detail.vue'

export default {
  components: {
    draggable,
    Detail,
    DialogWrapper
  },
  data () {
    return {
      acceptFileType: ACCEPT_FILE_TYPE,
      survey: {
        questions: []
      },
      uploader: {
        fileKeyPrefix: 'survey/image/',
        token: null,
        oldFile: {
          pc: null,
          mobile: null
        },
        newFile: {
          pc: null,
          mobile: null
        }
      },
      staticOptionTypes: STATIC_OPTION_TYPE,
      staticOptionTypeValues: _.map(STATIC_OPTION_TYPE, 'value'),
      optionTypeList: [],
      textQuestionDialog: {
        show: false,
        isEdit: false,
        hasNoSaveData: false,
        form: {
          question: {
            en: null,
            cn: null
          }
        },
        index: null
      },
      selectQuestionDialog: {
        show: false,
        isEdit: false,
        hasNoSaveData: false,
        form: {
          question: {
            en: null,
            cn: null
          },
          type: 'dropdown',
          optionType: null,
          filterI18nName: null,
          customOptions: {
            cn: null,
            en: null
          }
        },
        index: null
      }
    }
  },
  computed: {
    surveyId () {
      return this.$route.params.surveyId
    },
    isEnLanguage () {
      return this.$i18n.locale === 'en'
    },
    pcImagePath () {
      const path = this.survey.img_for_pc
      if (!path || path.includes('http')) {
        return path
      } else {
        return `${process.env.VUE_APP_QINIU_DOMAIN}${path}`
      }
    },
    mobileImagePath () {
      const path = this.survey.img_for_mobile
      if (!path || path.includes('http')) {
        return path
      } else {
        return `${process.env.VUE_APP_QINIU_DOMAIN}${path}`
      }
    },
    formRules () {
      const rule = {
        'question.en': [
          { required: true, message: this.$t('admin.crm.survey.message.questionNameRequired') }
        ],
        'question.cn': [
          { required: true, message: this.$t('admin.crm.survey.message.questionNameRequired') }
        ],
        type: [
          { required: true, message: this.$t('admin.crm.survey.message.questionTypeRequired') }
        ],
        optionType: [
          { required: true, message: this.$t('admin.crm.survey.message.optionTypeRequired') }
        ],
        'customOptions.cn': [
          { required: true, message: this.$t('admin.crm.survey.message.customOptionsRequired') },
          {
            validator: (rule, value, callback) => {
              let options = value.split('\n')
              options = _.filter(options, item => item !== '')
              if (options.length > 0) {
                let otherOptions = this.selectQuestionDialog.form.customOptions.en || ''
                otherOptions = otherOptions.split('\n')
                otherOptions = _.filter(otherOptions, item => item !== '')
                if (options.length !== otherOptions.length) {
                  callback(this.$t('admin.crm.survey.message.customOptionsNeedSameLine'))
                } else {
                  this.$refs.form.clearValidate('customOptions.en')
                  callback()
                }
              } else {
                callback(this.$t('admin.crm.survey.message.customOptionsRequired'))
              }
            }
          }
        ],
        'customOptions.en': [
          { required: true, message: this.$t('admin.crm.survey.message.customOptionsRequired') },
          {
            validator: (rule, value, callback) => {
              let options = value.split('\n')
              options = _.filter(options, item => item !== '')
              if (options.length > 0) {
                let otherOptions = this.selectQuestionDialog.form.customOptions.cn || ''
                otherOptions = otherOptions.split('\n')
                otherOptions = _.filter(otherOptions, item => item !== '')
                if (options.length !== otherOptions.length) {
                  callback(this.$t('admin.crm.survey.message.customOptionsNeedSameLine'))
                } else {
                  this.$refs.form.clearValidate('customOptions.cn')
                  callback()
                }
              } else {
                callback(this.$t('admin.crm.survey.message.customOptionsRequired'))
              }
            }
          }
        ]
      }
      return rule
    },
    textDialogTitleI18nKey () {
      return `admin.crm.survey.btn.${this.textQuestionDialog.isEdit ? 'editQuestion' : 'addQuestion'}.text`
    },
    selectDialogTitleI18nKey () {
      return `admin.crm.survey.btn.${this.selectQuestionDialog.isEdit ? 'editQuestion' : 'addQuestion'}.select`
    }
  },
  created () {
    this.initSurveyData()
  },
  methods: {
    ...mapActions('common', ['getQiniuUploadToken']),
    ...mapActions('survey', ['getSurveyById', 'updateSurvey']),
    ...mapActions('jobCustomField', ['searchJobCustomField']),
    ...mapGetters('account', ['getCurrentAccount']),
    async initSurveyData () {
      this.$eventBus.$emit('showLoading', true)
      try {
        const resp = await this.getSurveyById(this.surveyId)
        if (resp) {
          const survey = resp.survey
          if (survey.img_for_pc) {
            this.uploader.oldFile.pc = survey.img_for_pc
          }
          if (survey.img_for_mobile) {
            this.uploader.oldFile.mobile = survey.img_for_mobile
          }
          this.survey = resp.survey
        }
      } finally {
        this.$eventBus.$emit('showLoading', false)
      }
    },
    async initOptionData () {
      this.$eventBus.$emit('showLoading', true)
      try {
        const resp = await this.searchJobCustomField({ status: 1, type: 'SELECT,SINGLE' })
        if (resp) {
          const filterOptions = _.map(resp, (item) => {
            const vm = this
            return {
              ...item,
              get label () {
                const label = vm.isEnLanguage ? this.name : this.cn_name
                return label + vm.$t('admin.crm.survey.optionType.suffix')
              },
              get value () {
                return this.id
              }
            }
          })
          const tempTypes = this.staticOptionTypes.map(item => item)
          tempTypes.splice(3, 0, ...filterOptions)
          this.optionTypeList = tempTypes
        }
      } finally {
        this.$eventBus.$emit('showLoading', false)
      }
    },
    getQuestionIcon (questionItem) {
      switch (questionItem.question_type) {
        case 'dropdown':
          return 'iconInternalHiring-singleanswer'
        case 'checkbox':
          return 'iconInternalHiring-multipleanswer'
        case 'text':
          return 'iconInternalHiring-textfieldquestion'
      }
    },
    async openSelectQuestionDialog () {
      if (this.optionTypeList.length < 1) {
        this.initOptionData()
      }
      this.selectQuestionDialog.show = true
      if (!this.selectQuestionDialog.isEdit) {
        this.selectQuestionDialog.form = {
          question: {
            en: null,
            cn: null
          },
          type: 'dropdown',
          optionType: null,
          customOptions: {
            en: null,
            cn: null
          }
        }
      }
      this.$nextTick(() => {
        this.$refs.form.clearValidate()
      })
    },
    openTextQuestionDialog () {
      this.textQuestionDialog.show = true
      if (!this.textQuestionDialog.isEdit) {
        this.textQuestionDialog.form = {
          question: {
            en: null,
            cn: null
          },
          type: 'dropdown',
          optionType: null,
          customOptions: null
        }
      }
      this.$nextTick(() => {
        this.$refs.testQuestionForm.clearValidate()
      })
    },
    handleTextQuestionSave () {
      let result = false
      this.$refs.testQuestionForm.validate((valid) => {
        result = valid
      })
      if (!result) {
        return
      }
      const formData = this.textQuestionDialog.form
      const questionData = {
        cn_title: formData.question.cn,
        en_title: formData.question.en,
        question_type: 'text'
      }
      const index = this.textQuestionDialog.index
      if (this.textQuestionDialog.isEdit && index !== null && index >= 0 && index < this.survey.questions.length) {
        _.assign(this.survey.questions[index], questionData)
      } else {
        this.survey.questions.push(questionData)
      }

      this.textQuestionDialog.index = null
      this.textQuestionDialog.isEdit = false
      this.textQuestionDialog.show = false
    },
    handleSelectQuestionSave () {
      let result = false
      this.$refs.form.validate((valid) => {
        result = valid
      })
      if (!result) {
        return
      }
      const formData = this.selectQuestionDialog.form
      const questionData = {
        cn_title: formData.question.cn,
        en_title: formData.question.en,
        question_type: formData.type,
        option_type: formData.optionType
      }
      if (!this.staticOptionTypeValues.includes(formData.optionType)) {
        questionData.option_type = 'filter'
        questionData.filter = formData.optionType
      }
      if (formData.optionType === 'custom') {
        let enOptions = formData.customOptions.en.split('\n')
        enOptions = _.filter(enOptions, item => item !== '')
        let cnOptions = formData.customOptions.cn.split('\n')
        cnOptions = _.filter(cnOptions, item => item !== '')
        const options = []
        _.forEach(enOptions, (value, index) => {
          options.push({
            en_value: value,
            cn_value: cnOptions[index]
          })
        })
        questionData.options = options
      }
      const index = this.selectQuestionDialog.index
      if (this.selectQuestionDialog.isEdit && index !== null && index >= 0 && index < this.survey.questions.length) {
        _.assign(this.survey.questions[index], questionData)
      } else {
        this.survey.questions.push(questionData)
      }

      this.selectQuestionDialog.index = null
      this.selectQuestionDialog.isEdit = false
      this.selectQuestionDialog.show = false
    },
    async handleEditQuestion (item, index) {
      if (!item) {
        return
      }
      if (item.question_type === 'dropdown' || item.question_type === 'checkbox') {
        if (this.optionTypeList.length < 1) {
          await this.initOptionData()
        }
        const form = {
          question: {
            en: item.en_title,
            cn: item.cn_title
          },
          type: item.question_type,
          optionType: item.filter || item.option_type,
          filterI18nName: null,
          customOptions: {
            cn: null,
            en: null
          }
        }

        const existOptionType = _.find(this.optionTypeList, { value: form.optionType })
        if (existOptionType) {
          form.filterI18nName = existOptionType.label
        } else {
          const label = this.isEnLanguage ? item.custom_field_name : item.custom_field_cn_name
          form.filterI18nName = `${label}${this.$t('admin.crm.survey.label.disabled')}${this.$t('admin.crm.survey.optionType.suffix')}`
        }
        if (item.option_type === 'custom' && item.options) {
          const enOptions = _.map(item.options, 'en_value').join('\n')
          const cnOptions = _.map(item.options, 'cn_value').join('\n')
          form.customOptions = {
            en: enOptions,
            cn: cnOptions
          }
        }
        this.selectQuestionDialog.form = form
        this.selectQuestionDialog.index = index
        this.selectQuestionDialog.isEdit = true
        this.selectQuestionDialog.show = true
      } else if (item.question_type === 'text') {
        const form = {
          question: {
            en: item.en_title,
            cn: item.cn_title
          }
        }
        this.textQuestionDialog.form = form
        this.textQuestionDialog.index = index
        this.textQuestionDialog.isEdit = true
        this.textQuestionDialog.show = true
      }
      this.$nextTick(() => {
        this.$refs.form.clearValidate()
      })
    },
    handleDeleteQuestion (item, index) {
      this.survey.questions.splice(index, 1)
    },
    handleCancel () {
      this.$router.push('/v-admin/crm-management/survey/list')
    },
    validate () {
      let hasImage = false
      const newPcImage = this.uploader.newFile.pc
      const newMobileImage = this.uploader.newFile.mobile
      if (newPcImage && newMobileImage) {
        hasImage = true
      }
      if (!hasImage && this.survey.img_for_pc && this.survey.img_for_mobile) {
        hasImage = true
      }
      if (!hasImage) {
        this.$message.error({
          type: 'error',
          showClose: true,
          message: this.$t('admin.crm.survey.message.imageRequired')
        })
        return false
      }
      if (this.survey.questions.length < 1) {
        this.$message.error({
          type: 'error',
          showClose: true,
          message: this.$t('admin.crm.survey.message.questionRequired')
        })
        return false
      }

      return true
    },
    async handleSave () {
      const result = this.validate()
      if (!result) {
        return
      }
      this.$eventBus.$emit('showLoading', true)
      try {
        const survey = {
          ...this.survey
        }
        const newPcImage = this.uploader.newFile.pc
        const newMobileImage = this.uploader.newFile.mobile
        if (newPcImage) {
          survey.img_for_pc = await this.uploadFileToCDN(newPcImage)
        }
        if (newMobileImage) {
          survey.img_for_mobile = await this.uploadFileToCDN(newMobileImage)
        }
        _.forEach(survey.questions, (item, index) => {
          item.order = index
        })
        const resp = await this.updateSurvey(survey)
        if (resp) {
          this.$router.push('/v-admin/crm-management/survey/list')
        }
      } finally {
        this.$eventBus.$emit('showLoading', false)
      }
    },
    handleUploaderClick (isPc) {
      let fileInput = null
      if (isPc) {
        fileInput = this.$refs.pcImageFile
      } else {
        fileInput = this.$refs.mobileImageFile
      }
      fileInput.click()
    },
    handleImageFileChange (isPc) {
      let fileInput = null
      if (isPc) {
        fileInput = this.$refs.pcImageFile
      } else {
        fileInput = this.$refs.mobileImageFile
      }
      const file = fileInput.files[0]
      if (!file) {
        return
      }
      if (!ACCEPT_FILE_TYPE.includes(file.type)) {
        this.$message.error({
          type: 'error',
          showClose: true,
          message: this.$t('admin.crm.survey.message.wrongImageType')
        })
        return
      }
      if (file.size > ACCEPT_FILE_SIZE) {
        this.$message.error({
          type: 'error',
          showClose: true,
          message: this.$t('admin.crm.survey.message.wrongImageSize', { size: 5 })
        })
        return
      }
      const fileTempUrl = window.URL.createObjectURL(file)
      if (isPc) {
        this.survey.img_for_pc = fileTempUrl
        this.uploader.newFile.pc = file
      } else {
        this.survey.img_for_mobile = fileTempUrl
        this.uploader.newFile.mobile = file
      }
    },
    async uploadFileToCDN (file) {
      const fileKey = this.generateFileKey(file)
      if (!this.uploader.token) {
        const tokenResp = await this.getQiniuUploadToken({ is_public: true })
        this.uploader.token = tokenResp.token
      }
      return new Promise((resolve, reject) => {
        const observable = qiniu.upload(file, fileKey, this.uploader.token, {}, { region: qiniu.region.z0 })
        observable.subscribe(
          (resp) => {
            // in progress
          },
          (resp) => {
            // error
            if (resp && resp.message) {
              this.$message.error({
                type: 'error',
                showClose: true,
                message: this.$t('admin.crm.survey.message.uploadError')
              })
              this.uploader.token = null
            }
            reject(resp)
          },
          (resp) => {
            // complete
            resolve(fileKey)
          }
        )
      })
    },
    generateFileKey (file) {
      return `${this.uploader.fileKeyPrefix}${this.survey.id}/${this.getCurrentAccount().id}/${new Date().valueOf()}`
    },
    handleTextQuestionCancelOrClose () {
      this.textQuestionDialog.index = null
      this.textQuestionDialog.isEdit = false
    },
    handleSelectQuestionCancelOrClose () {
      this.selectQuestionDialog.index = null
      this.selectQuestionDialog.isEdit = false
    }
  }
}
</script>

<style lang="scss" scoped>
.survey-editor {
  position: relative;

  .image-uploader {
    display: flex;
    height: 150px;
    margin-top: 20px;

    > div {
      background: #f7f7f7;
      position: relative;

      &.no-image {
        .image-previewer {
          display: none;
        }

        .uploader-mask {
          display: flex;
        }
      }

      &:not(.no-image):hover {
        .uploader-mask {
          display: flex;
          background: rgba($color: #f7f7f7, $alpha: 0.8);
        }
      }

      .image-previewer {
        width: 100%;
        height: 100%;
      }

      .uploader-mask {
        display: none;
        width: 100%;
        height: 100%;
        text-align: center;
        position: absolute;
        top: 0;
        left: 0;
        cursor: pointer;

        .uploader {
          margin: auto;

          .add-btn {
            color: #0096cc;
            font-size: 46px;
          }

          .tip-message {
            margin-top: 5px;

            .tip-title {
              font-weight: 500;
              font-size: 14px;
            }

            .tip-subtitle {
              margin-top: 5px;
              color: #888888;
            }
          }
        }
      }
    }
    .pc-image {
      flex: 1;
    }
    .mobile-image {
      width: 230px;
      margin-left: 10px;
    }
  }

  .question-list-container {
    margin-top: 20px;
    margin-bottom: 60px;

    .question-list {
      .list-item {
        padding: 5px 10px;
        border: 1px solid #e7e7e7;
        background: #f1f1f1;

        &:not(:first-child) {
          margin-top: 10px;
        }

        &.ghost {
          opacity: 0.5;
          background-color: #cecece;
        }

        .item-title {
          display: flex;
          line-height: 20px;

          .item-icon {
            i {
              font-size: 22px;
              color: #b2b2b2;
              vertical-align: middle;
            }
          }

          .item-name {
            flex: 1;
            margin-left: 10px;
            line-height: 22px;
            color: #5c5c5c;
          }

          .item-operator-container {
            color: #0096cc;
            font-size: 16px;

            .drag-btn {
              display: inline;
              cursor: grab;
              vertical-align: middle;

              i {
                font-size: 22px;
              }
            }

            .edit-btn {
              display: inline;
              margin-left: 10px;
              cursor: pointer;
              vertical-align: middle;

              i {
                font-size: 18px;
              }
            }

            .delete-btn {
              display: inline;
              margin-left: 10px;
              cursor: pointer;
              vertical-align: middle;

              i {
                font-size: 22px;
              }
            }
          }
        }

        .item-content {
          color: #676767;

          .content-list {
            padding-left: 55px;

            .content-item {
              padding: 3px 0;

              &::before {
                content: '-';
                margin-right: 10px;
              }
            }
          }
        }
      }
    }

    .add-btn-group {
      > div {
        margin-top: 15px;
        border: 1px dashed #8a8a8a;
        line-height: 32px;
        text-align: center;
        color: #0096cc;
        font-size: 14px;
        cursor: pointer;
      }
    }
  }

  .bottom-btn-group {
    display: flex;
    margin-top: 24px;
    justify-content: flex-end;

    .el-button {
      padding: 8px 28px;
      border-radius: 2px;
      line-height: 22px;
      font-size: 16px;
      font-weight: 500;
      color: #0096cc;

      &:last-child {
        margin-left: 24px;
        margin-right: 0;
        background: #0096cc;
        border-color: #0096cc;
        color: #ffffff;
      }
    }
  }

  .text-question-dialog-body {
    .el-form {
      ::v-deep {
        .el-form-item {
          &:not(.is-no-asterisk) {
            .el-form-item__label {

              &::after {
                content: '*';
                margin: 9px 0 0 3px;
                color: #f56c6c;
                line-height: 1;
                visibility: hidden;
              }
            }
          }

          &.is-required:not(.is-no-asterisk) {
            .el-form-item__label {
              &::before {
                display: none;
              }

              &::after {
                visibility: unset;
              }
            }
          }
        }
        .el-select {
          width: auto
        }

        .el-form-item__label {
          font-weight: 500;
        }
      }
    }
  }

  .select-question-dialog-body {
    .el-form {
      ::v-deep {
        .el-form-item {
          &:not(.is-no-asterisk) {
            .el-form-item__label {

              &::after {
                content: '*';
                margin: 9px 0 0 3px;
                color: #f56c6c;
                line-height: 1;
                visibility: hidden;
              }
            }
          }

          &.is-required:not(.is-no-asterisk) {
            .el-form-item__label {
              &::before {
                display: none;
              }

              &::after {
                visibility: unset;
              }
            }
          }
        }

        .el-select {
          width: auto
        }

        .el-form-item__label {
          font-weight: 500;
        }
      }

      .label-tip {
        p {
          margin: -10px 0 10px;
          line-height: initial;
          word-break: break-word;
        }
      }

      .option-type-select {
        width: 30%;
      }
    }
  }
}
</style>
